import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { ApiError, api } from '../api/client';
import { Alert, LoadingBlock } from '../components/ui';
import { LAGRINGSTID_MAANEDER } from '../lib/lagringstid';
import { SLETTEFRIST_TIMER } from '../lib/gjesterettigheter';
import { SELSKAP } from '../lib/selskap';

/**
 * Gjestens rettigheter, på gjestens egen lenke.
 *
 * Etter rollebyttet er det vi som er behandlingsansvarlige for opplysningene om
 * gjestene. Da er det oss de spør, og en rett som forutsetter at man finner en
 * e-postadresse i en erklæring man aldri har lest, blir aldri brukt.
 *
 * Siden viser aldri opplysningene. Svaret kommer på e-post, til adressen som
 * står på gjesten – svarlenka er et bærertoken som kan ha vært videresendt, og
 * en side som viste allergien til den som har lenka, ville tatt tilbake det
 * hele svarsida er bygget for å beskytte.
 *
 * Den samme siden tar imot bekreftelseslenka fra e-posten, på `?kode=`. Ett
 * sted, fordi det er én sak: «hva har dere om meg, og kan jeg bli kvitt det».
 */
export function RsvpRettigheterPage() {
  const { token } = useParams<{ token: string }>();
  const [param, setParam] = useSearchParams();
  const kode = param.get('kode');

  const [travel, setTravel] = useState(false);
  const [feil, setFeil] = useState<string | null>(null);
  /** Hva som nettopp skjedde. Står til siden lastes på nytt. */
  const [kvittert, setKvittert] = useState<string | null>(null);
  /** Slettingen er gjennomført. Da er det ingenting mer å gjøre her. */
  const [slettet, setSlettet] = useState(false);
  /** Bekreftelsen fra e-posten, mens den går. */
  const [bekrefter, setBekrefter] = useState(false);

  const be = async (hva: 'innsyn' | 'sletting') => {
    setTravel(true);
    setFeil(null);
    setKvittert(null);
    try {
      const svar = await api.post<{ sendtTil: string }>(`/rsvp/${token}/${hva}`);
      setKvittert(
        hva === 'innsyn'
          ? `Vi har sendt kopien til ${svar.sendtTil}. Den kommer om et øyeblikk.`
          : `Vi har sendt en bekreftelse til ${svar.sendtTil}. Åpne den innen ` +
            `${SLETTEFRIST_TIMER} timer, så sletter vi deg. Skjer det ingenting, står alt som før.`,
      );
    } catch (fanget) {
      setFeil(fanget instanceof ApiError ? fanget.message : 'Noe gikk galt. Prøv igjen.');
    } finally {
      setTravel(false);
    }
  };

  /*
    Bekreftelsen kjøres av seg selv når koden står i adressen.

    Gjesten har allerede tatt valget – i e-posten sto det «Ja, slett meg», og
    et skjermbilde til med enda en knapp ville vært å spørre om det samme to
    ganger. Det som betyr noe, er at hun kom hit fra innboksen sin.
  */
  useEffect(() => {
    if (!kode || !token) return;
    let avbrutt = false;
    setBekrefter(true);
    api
      .post(`/rsvp/${token}/sletting/bekreft`, { kode })
      .then(() => {
        if (avbrutt) return;
        setSlettet(true);
        // Koden ut av adressefeltet når den er brukt: den virker ikke lenger,
        // og en lenke i historikken som ser ut til å slette noe, er en lenke
        // noen trykker på igjen og blir forvirret av.
        setParam({}, { replace: true });
      })
      .catch((fanget: unknown) => {
        if (avbrutt) return;
        setFeil(fanget instanceof ApiError ? fanget.message : 'Noe gikk galt. Prøv igjen.');
      })
      .finally(() => {
        if (!avbrutt) setBekrefter(false);
      });
    return () => {
      avbrutt = true;
    };
    // `setParam` er stabil nok, og koden er det eneste som skal utløse dette.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kode, token]);

  if (slettet) {
    return (
      <div className="rsvp-page">
        <div className="rsvp-card">
          <h1 style={{ fontSize: '1.6rem' }}>Nå er du slettet</h1>
          <p className="muted">
            Navnet ditt, kontaktopplysningene, det du hadde oppgitt om mat og svaret ditt er borte
            fra gjestelista. Brudeparet får beskjed om at du er ute av lista, men ikke hvorfor.
          </p>
          <p className="muted">
            Svarlenken din virker ikke lenger. Ombestemmer du deg, må du si det til brudeparet
            selv – vi kan ikke legge deg inn igjen.
          </p>
        </div>
      </div>
    );
  }

  if (bekrefter) {
    return (
      <div className="rsvp-page">
        <div className="rsvp-card">
          <LoadingBlock label="Bekrefter …" />
        </div>
      </div>
    );
  }

  return (
    <div className="rsvp-page">
      <div className="rsvp-card">
        <p className="eyebrow" style={{ justifyContent: 'center' }}>
          Opplysningene om deg
        </p>
        <h1 style={{ fontSize: '1.6rem', marginTop: '0.4rem' }}>Hva vil du gjøre?</h1>

        <p className="muted">
          Det er <strong>{SELSKAP.navn}</strong> som er ansvarlig for opplysningene om gjestene i
          dette bryllupet – ikke brudeparet. Derfor er det oss du ber, og du kan gjøre det her.
        </p>

        {feil ? <Alert>{feil}</Alert> : null}
        {kvittert ? <Alert kind="success">{kvittert}</Alert> : null}

        <div className="gjesterett">
          <section>
            <h2>Se hva vi har om deg</h2>
            <p>
              Vi sender en kopi av alt som står registrert på deg – navn, kontaktopplysninger, det
              som er oppgitt om mat, eventuelle notater og svaret ditt.
            </p>
            <p className="tiny muted">
              Kopien går til e-postadressen som står på deg, ikke til denne siden. Det er med
              vilje: lenken du er inne på, kan ha blitt videresendt, og da skal ikke den som har
              den, få lese allergien din.
            </p>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => void be('innsyn')}
              disabled={travel}
            >
              Send meg en kopi
            </button>
          </section>

          <section>
            <h2>Bli slettet</h2>
            <p>
              Vi fjerner alt vi har om deg i dette bryllupet. Brudeparet får beskjed om at du er
              ute av lista, men ikke hvorfor, og adressen din kan ikke legges inn igjen.
            </p>
            <p className="tiny muted">
              Du får først en e-post du må bekrefte i, som varer i {SLETTEFRIST_TIMER} timer.
              Ingenting skjer før du har trykket der.
            </p>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => void be('sletting')}
              disabled={travel}
            >
              Be om å bli slettet
            </button>
          </section>

          <section>
            <h2>Rette noe</h2>
            <p>
              Skrivefeil i navnet, eller en allergi som har endret seg? Det raskeste er å endre
              svaret ditt, eller å si det til brudeparet – de retter det med én gang.
            </p>
            <p>
              <Link to={`/rsvp/${token}`}>Tilbake til svaret mitt</Link>
            </p>
          </section>
        </div>

        <p className="rsvp-personvern" style={{ marginTop: '1.5rem' }}>
          Vi sletter opplysningene om gjestene {LAGRINGSTID_MAANEDER} måneder etter bryllupsdagen
          av oss selv, uten at du trenger å be om det. Har du spørsmål, eller står det ingen
          e-postadresse på deg, når du oss på{' '}
          <a href={`mailto:${SELSKAP.epost}`}>{SELSKAP.epost}</a>. Du kan også klage til
          Datatilsynet.{' '}
          <Link to="/gjesteinformasjon">Mer om hva vi lagrer og hvorfor</Link>
        </p>
      </div>
    </div>
  );
}
